import { type PlayerController } from '../player/controller.js';
import { SPEED_OPTIONS } from '../config.js';

export type KeyboardActions = {
  player: PlayerController;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomReset: () => void;
};

const EDITABLE_TAGS = [ 'INPUT', 'SELECT', 'TEXTAREA' ];

const isEditable = (target: EventTarget | null): boolean => {
  if (target === null || typeof target !== 'object' || !('tagName' in target)) {
    return false;
  }
  const element = target as { tagName: string; isContentEditable?: boolean };
  return EDITABLE_TAGS.includes(element.tagName) || element.isContentEditable === true;
};

export const handleKey = (event: KeyboardEvent, actions: KeyboardActions): boolean => {
  if (isEditable(event.target) || event.metaKey || event.ctrlKey || event.altKey) {
    return false;
  }
  const { player } = actions;
  switch (event.key) {
    case ' ':
      player.toggle();
      return true;
    case 'ArrowLeft':
      player.prev();
      return true;
    case 'ArrowRight':
      player.next();
      return true;
    case 'Home':
      player.first();
      return true;
    case 'End':
      player.last();
      return true;
    case 'p':
    case 'P':
      player.nextProduct();
      return true;
    case '+':
    case '=':
      actions.zoomIn();
      return true;
    case '-':
    case '_':
      actions.zoomOut();
      return true;
    case '0':
      actions.zoomReset();
      return true;
    default: {
      const digit = Number.parseInt(event.key, 10);
      const speed = SPEED_OPTIONS[digit - 1];
      if (digit >= 1 && speed !== undefined) {
        player.setSpeed(speed);
        return true;
      }
      return false;
    }
  }
};

export const attachKeyboard = (target: Window, actions: KeyboardActions): () => void => {
  const listener = (event: KeyboardEvent): void => {
    if (handleKey(event, actions)) {
      event.preventDefault();
    }
  };
  target.addEventListener('keydown', listener);
  return () => target.removeEventListener('keydown', listener);
};
